import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    Input,
} from '@pagespeedify/ui';
import {
    ImageIcon,
    CheckCircle2,
    XCircle,
    Loader2,
    Clock,
    Search,
} from 'lucide-react';
import { Task } from '../../types';

interface ImageTaskQuickNavProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentTaskId?: string;
    onTaskSelect?: (taskId: string) => void;
    tasks?: Task[];
}

export function ImageTaskQuickNav({ open, onOpenChange, currentTaskId, onTaskSelect, tasks = [] }: ImageTaskQuickNavProps) {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const filteredTasks = tasks.filter(task => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            (task.title || '').toLowerCase().includes(searchLower) ||
            task.id.toLowerCase().includes(searchLower)
        );
    });

    // Reset selection when filter changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [search]);

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setSearch('');
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    const handleSelect = (taskId: string) => {
        if (onTaskSelect) {
            onTaskSelect(taskId);
        } else {
            navigate('/image-optimization', { state: { taskId } });
        }
        onOpenChange(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < filteredTasks.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredTasks[selectedIndex]) {
                    handleSelect(filteredTasks[selectedIndex].id);
                }
                break;
            case 'Escape':
                e.preventDefault();
                onOpenChange(false);
                break;
        }
    };

    // Scroll selected item into view
    useEffect(() => {
        if (listRef.current) {
            const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
            case 'resolved':
            case 'success':
                return <CheckCircle2 className="w-4 h-4 text-green-500" />;
            case 'failed':
            case 'closed':
                return <XCircle className="w-4 h-4 text-red-500" />;
            case 'processing':
            case 'in_progress':
                return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
            default:
                return <Clock className="w-4 h-4 text-yellow-500" />;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent hideCloseButton className="w-[95vw] max-w-2xl max-h-[85vh] sm:max-h-[80vh] p-0 gap-0 overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between border-b px-4 py-2">
                    <h2 className="text-base font-semibold">Navigate</h2>
                    <div className="flex items-center gap-2">
                        <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                            ESC
                        </kbd>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="flex items-center border-b px-3">
                    <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                        ref={inputRef}
                        type="text"
                        placeholder="Search image optimizations..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-9"
                    />
                </div>

                {/* List Content */}
                <div
                    ref={listRef}
                    className="max-h-[400px] overflow-y-auto overflow-x-hidden p-2"
                >
                    {filteredTasks.length === 0 ? (
                        <div className="px-4 py-12 text-center text-muted-foreground">
                            No optimizations found matching "{search}"
                        </div>
                    ) : (
                        filteredTasks.map((task, index) => {
                            const isSelected = index === selectedIndex;
                            const isCurrent = task.id === currentTaskId;

                            return (
                                <button
                                    key={task.id}
                                    onClick={() => handleSelect(task.id)}
                                    className={`
                                        w-full px-3 py-2 flex items-center justify-between gap-2 text-left transition-colors rounded-md min-w-0 mb-1
                                        ${isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'}
                                    `}
                                >
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className={`
                                            flex items-center justify-center w-8 h-8 rounded-full shrink-0
                                            ${isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                                        `}>
                                            <ImageIcon className="w-4 h-4" />
                                        </div>
                                        <div className="flex flex-col overflow-hidden min-w-0">
                                            <span className={`truncate font-medium text-sm ${isCurrent ? 'text-primary' : ''}`}>
                                                {task.title || `Optimization ${new Date(task.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                                            </span>
                                            <span className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                                {new Date(task.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                                {task.context && task.context.file_count ? (
                                                    <>
                                                        <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground" />
                                                        <span>{task.context.file_count} files</span>
                                                    </>
                                                ) : null}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {getStatusIcon(task.status)}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground bg-muted/30">
                    <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                            <kbd className="inline-flex h-4 select-none items-center rounded border bg-background px-1 font-mono text-[10px]">↑</kbd>
                            <kbd className="inline-flex h-4 select-none items-center rounded border bg-background px-1 font-mono text-[10px]">↓</kbd>
                            navigate
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="inline-flex h-4 select-none items-center rounded border bg-background px-1 font-mono text-[10px]">↵</kbd>
                            select
                        </span>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
